import { displayCategoryName, displayName } from "~/common/functions";
import type {
  CompoundCompoundRecordsBoolExp,
  CompoundCompoundRecordsOrderBy,
  OrderBy,
  StringComparisonExp,
} from "~/common/types/graphql";
import type { GetCompoundEntitiesQueryVariables } from "~/features/compoundEntities/gqls";
import type {
  CompoundDetailRaw,
  CompoundDetailUI,
  CompoundKeyType,
  CompoundRaw,
  CompoundRow,
  CompoundSearchCondition,
  CompoundSourceRecordRaw,
  CompoundSourceRecordUI,
} from "~/features/compoundEntities/types";
import type { Locale } from "~/i18n/resolveLocale";
import { parseGdtfRange } from "~/utils/gdtfRange";

/**
 * Best-effort display name for a compound record, ported from V1 `getEntityName`:
 * locale display name → composed `properties.name` → `record=` id slug → "-".
 */
function compoundDisplayName(raw: Pick<CompoundRaw, "name" | "nameJa" | "key" | "properties">, locale: Locale): string {
  const resolved = displayName({ name: raw.name, nameJa: raw.nameJa }, locale);
  if (resolved) return resolved;

  const props = (raw.properties ?? {}) as Record<string, unknown>;
  if (typeof props.name === "string" && props.name) return props.name;

  if (raw.key) {
    const match = raw.key.match(/record=([^&]+)/);
    if (match) return `Record: ${match[1].substring(0, 8)}...`;
  }
  return "-";
}

function toCompoundRow(raw: CompoundRaw, locale: Locale): CompoundRow {
  return {
    id: raw.id,
    key: raw.key,
    name: compoundDisplayName(raw, locale),
    recordType: raw.recordType,
    createdAt: String(raw.createdAt),
    updatedAt: String(raw.updatedAt),
  };
}

export function toCompoundRowList(raws: readonly CompoundRaw[], locale: Locale): CompoundRow[] {
  return raws.map((raw) => toCompoundRow(raw, locale));
}

export function toCompoundDetailUI(raw: CompoundDetailRaw, locale: Locale): CompoundDetailUI {
  return {
    id: raw.id,
    key: raw.key,
    recordType: raw.recordType,
    displayName: compoundDisplayName(raw, locale),
    name: raw.name ?? "",
    nameJa: raw.nameJa ?? "",
    date: raw.date ?? "",
    endDate: raw.endDate ?? "",
    createdAt: String(raw.createdAt),
    updatedAt: String(raw.updatedAt),
    version: raw.v,
    sourceId: raw.sourceId ?? "",
    targetId: raw.targetId ?? "",
    categoryNameArray: raw.categoryNameArray,
    categoryIdArray: raw.categoryIdArray,
    recordIdArray: raw.recordIdArray,
    nameArray: raw.nameArray,
    nameJaArray: raw.nameJaArray,
    dateArray: raw.dateArray,
    endDateArray: raw.endDateArray,
    referenceArray: raw.referenceArray as unknown[],
    properties: (raw.properties ?? {}) as Record<string, unknown>,
    metadata: raw.metadata,
  };
}

export function toCompoundSourceRecordUIList(
  raws: readonly CompoundSourceRecordRaw[],
  recordIds: readonly string[],
  locale: Locale,
): CompoundSourceRecordUI[] {
  const byId = new Map(raws.map((raw) => [raw.id, raw]));

  return recordIds.map((id) => {
    const raw = byId.get(id);
    return {
      id,
      displayName: raw ? displayName(raw, locale) || id : id,
      name: raw?.name ?? "",
      nameJa: raw?.nameJa ?? "",
      date: raw?.date ?? "",
      endDate: raw?.endDate ?? "",
      categoryNames:
        raw?.recordMetadata.recordMetadataCategories.map(({ category }) =>
          displayCategoryName(
            { name: category.name, i18nLabel: category.i18nLabel as Record<string, unknown> | null },
            locale,
          ),
        ) ?? [],
    };
  });
}

// ── where / variables ─────────────────────────────────────────────────────────

const KEY_PATTERNS = {
  typeEntity: "%type=entity%",
  typeRecord: "type=%&record=%",
  categories: "%categories=%",
  wikidata: "%wikidata=%",
  pipe: "%|%",
} as const;

const textCondition = (value: string, exactMatch: boolean): StringComparisonExp =>
  exactMatch ? { _eq: value } : { _ilike: `%${value}%` };

const COMPOUND_KEY_TYPES: readonly CompoundKeyType[] = ["default-wikidata", "default-individual", "custom"];
const isCompoundKeyType = (value: string): value is CompoundKeyType =>
  (COMPOUND_KEY_TYPES as readonly string[]).includes(value);

/** The three `key`-shape buckets from V1 SearchControls (`compoundKeyType`). */
const compoundKeyTypeCondition = (keyType: CompoundKeyType): CompoundCompoundRecordsBoolExp => {
  switch (keyType) {
    case "default-wikidata":
      return {
        _and: [
          { key: { _like: KEY_PATTERNS.typeEntity } },
          { key: { _like: KEY_PATTERNS.categories } },
          { key: { _like: KEY_PATTERNS.wikidata } },
        ],
      };
    case "default-individual":
      return {
        _and: [
          { key: { _like: KEY_PATTERNS.typeRecord } },
          { _not: { key: { _like: KEY_PATTERNS.pipe } } },
          { _not: { key: { _like: KEY_PATTERNS.categories } } },
          { _not: { key: { _like: KEY_PATTERNS.wikidata } } },
        ],
      };
    case "custom":
      return {
        _and: [
          { key: { _like: KEY_PATTERNS.typeRecord } },
          { key: { _like: KEY_PATTERNS.pipe } },
          { _not: { key: { _like: KEY_PATTERNS.categories } } },
          { _not: { key: { _like: KEY_PATTERNS.wikidata } } },
        ],
      };
  }
};

/**
 * dateJdn/endDateJdn overlap filter for a GDTF range (same semantics as records).
 * Records without a dateJdn are excluded when a GDTF filter is active. Ported from V1.
 */
const buildGdtfRangeWhere = (gdtfFrom?: string, gdtfTo?: string): CompoundCompoundRecordsBoolExp[] => {
  const parsed = parseGdtfRange(gdtfFrom, gdtfTo);
  if (!parsed.hasFilter || parsed.inverted) return [];

  const conditions: CompoundCompoundRecordsBoolExp[] = [{ dateJdn: { _isNull: false } }];
  if (parsed.toJdn != null) {
    conditions.push({ dateJdn: { _lte: parsed.toJdn } });
  }
  if (parsed.fromJdn != null) {
    conditions.push({
      _or: [
        { endDateJdn: { _gte: parsed.fromJdn } },
        { _and: [{ endDateJdn: { _isNull: true } }, { dateJdn: { _gte: parsed.fromJdn } }] },
      ],
    });
  }
  return conditions;
};

/**
 * Translate the search conditions (+ selected version + GDTF range) into a Hasura where-exp.
 * `id` conditions are always exact (`uuid` column); other text fields honour the exact-match flag.
 */
export function buildCompoundWhere(
  conditions: readonly CompoundSearchCondition[],
  version?: string,
  gdtfFrom?: string,
  gdtfTo?: string,
): CompoundCompoundRecordsBoolExp {
  const andList: CompoundCompoundRecordsBoolExp[] = [];

  for (const c of conditions) {
    const value = c.value.trim();
    if (!value) continue;
    switch (c.field) {
      case "id":
        andList.push({ id: { _eq: value } });
        break;
      case "key":
        andList.push({ key: textCondition(value, c.exactMatch) });
        break;
      case "name":
        andList.push({ name: textCondition(value, c.exactMatch) });
        break;
      case "nameJa":
        andList.push({ nameJa: textCondition(value, c.exactMatch) });
        break;
      case "recordType":
        andList.push({ recordType: textCondition(value, c.exactMatch) });
        break;
      case "compoundKeyType":
        if (isCompoundKeyType(value)) andList.push(compoundKeyTypeCondition(value));
        break;
    }
  }

  if (version) andList.push({ v: { _eq: version } });
  andList.push(...buildGdtfRangeWhere(gdtfFrom, gdtfTo));

  return { _and: andList };
}

/** Compound records are always newest-first (V1 hardcoded `createdAt DESC`, no sort UI). */
const COMPOUND_ORDER_BY: CompoundCompoundRecordsOrderBy[] = [{ createdAt: "DESC" as OrderBy }];

/** Assemble the full `GetCompoundEntities` query variables for a page of the list. */
export function buildCompoundVariables(args: {
  tenantId: string;
  conditions: readonly CompoundSearchCondition[];
  version?: string;
  gdtfFrom?: string;
  gdtfTo?: string;
  pageIndex: number;
  pageSize: number;
}): GetCompoundEntitiesQueryVariables {
  const { tenantId, conditions, version, gdtfFrom, gdtfTo, pageIndex, pageSize } = args;
  return {
    tenantId,
    limit: pageSize,
    offset: pageIndex * pageSize,
    where: buildCompoundWhere(conditions, version, gdtfFrom, gdtfTo),
    orderBy: COMPOUND_ORDER_BY,
  };
}
